import React, { useEffect, useRef } from "react";
import { FaLink } from "react-icons/fa";
import { sanitizeDocumentTemplateHtml } from "../security/documentTemplateHtml";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeHref = (raw) => {
  let value = String(raw || "").trim();
  if (!value) return "";
  if (/^(?:mailto:|tel:)/i.test(value)) return value;
  if (!/^https?:\/\//i.test(value)) value = "https://" + value;
  try {
    const parsed = new URL(value);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.toString() : "";
  } catch {
    return "";
  }
};

export default function MarketingEmailEditor({ value, onChange, minHeight = 320 }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const safe = sanitizeDocumentTemplateHtml(value);
    if (ref.current.innerHTML !== safe) ref.current.innerHTML = safe;
  }, [value]);

  const sync = () => onChange(sanitizeDocumentTemplateHtml(ref.current?.innerHTML || ""));
  const command = (name, commandValue = null) => {
    ref.current?.focus();
    document.execCommand(name, false, commandValue);
    sync();
  };

  const selectionRange = () => {
    const selection = window.getSelection?.();
    if (!selection?.rangeCount || !ref.current) return null;
    const range = selection.getRangeAt(0);
    if (!ref.current.contains(range.commonAncestorContainer)) return null;
    return { range: range.cloneRange(), text: selection.toString() };
  };

  const restoreRange = (saved) => {
    if (!saved?.range) return;
    const selection = window.getSelection?.();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(saved.range);
  };

  const insertLink = () => {
    const saved = selectionRange();
    const rawUrl = window.prompt("Cole o endereço do link:");
    if (!rawUrl) return;
    const href = normalizeHref(rawUrl);
    if (!href) {
      window.alert("Informe um link válido.");
      return;
    }
    const label = saved?.text?.trim() || window.prompt("Texto que será exibido:", "Saiba mais");
    if (!label) return;
    ref.current?.focus();
    restoreRange(saved);
    document.execCommand(
      "insertHTML",
      false,
      `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="color:#0b63ce;text-decoration:underline;font-weight:600">${escapeHtml(label)}</a>`,
    );
    sync();
  };

  const insertButton = () => {
    const label = window.prompt("Texto do botão:", "Saiba mais");
    if (!label) return;
    const rawUrl = window.prompt("Link do botão:");
    if (!rawUrl) return;
    const href = normalizeHref(rawUrl);
    if (!href) {
      window.alert("Informe um link válido.");
      return;
    }
    ref.current?.focus();
    document.execCommand(
      "insertHTML",
      false,
      `<p><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;background-color:#0b63ce;color:#ffffff;padding:12px 18px;border-radius:10px;text-decoration:none;font-weight:700">${escapeHtml(label)}</a></p>`,
    );
    sync();
  };

  const toolbarButton = "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-600 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700";

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50/50 p-2">
        <select aria-label="Estilo do texto" defaultValue="p" onChange={(event) => command("formatBlock", event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 outline-none">
          <option value="p">Texto</option>
          <option value="h2">Título</option>
          <option value="h3">Subtítulo</option>
        </select>
        <select aria-label="Tamanho da fonte" defaultValue="3" onChange={(event) => command("fontSize", event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 outline-none">
          <option value="2">Pequeno</option>
          <option value="3">Normal</option>
          <option value="4">Médio</option>
          <option value="5">Grande</option>
        </select>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); command("bold"); }} className={toolbarButton}>B</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); command("italic"); }} className={toolbarButton}><em>I</em></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); command("underline"); }} className={toolbarButton}><u>U</u></button>
        <span className="mx-1 h-6 w-px bg-slate-200" />
        <button type="button" title="Inserir link" onMouseDown={(e) => { e.preventDefault(); insertLink(); }} className={toolbarButton}><FaLink className="mr-1 inline" /> Link</button>
        <button type="button" title="Inserir botão com link" onMouseDown={(e) => { e.preventDefault(); insertButton(); }} className={toolbarButton}>▣ Botão</button>
        <span className="mx-1 h-6 w-px bg-slate-200" />
        <button type="button" title="Alinhar à esquerda" onMouseDown={(e) => { e.preventDefault(); command("justifyLeft"); }} className={toolbarButton}>≡</button>
        <button type="button" title="Centralizar" onMouseDown={(e) => { e.preventDefault(); command("justifyCenter"); }} className={toolbarButton}>≣</button>
        <button type="button" title="Alinhar à direita" onMouseDown={(e) => { e.preventDefault(); command("justifyRight"); }} className={toolbarButton}>≡↦</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); command("insertUnorderedList"); }} className={toolbarButton}>• Lista</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); command("insertOrderedList"); }} className={toolbarButton}>1. Lista</button>
        <label className="ml-1 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-500">
          Cor
          <input type="color" defaultValue="#172033" onInput={(event) => command("foreColor", event.target.value)} className="h-6 w-7 cursor-pointer border-0 bg-transparent p-0" />
        </label>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); command("removeFormat"); }} className={toolbarButton}>Limpar formato</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        data-placeholder="Escreva o e-mail..."
        style={{ minHeight }}
        className="p-6 text-sm leading-relaxed text-slate-800 outline-none empty:before:text-slate-300 empty:before:content-[attr(data-placeholder)]"
      />
    </div>
  );
}
